import * as React from 'react';
import {
  View,
  Text,
  Linking,
  StyleSheet,
  Dimensions,
  EmitterSubscription,
  Platform
} from 'react-native';
import { translate } from 'secullum-i18n';
import {
  TextBox,
  Space,
  Button,
  ErrorMessage,
  Message,
  CheckBox,
  getLocale,
  isTablet
} from 'secullum-react-native-ui';
import { Form } from '@secullum/react-native-autofocus';
import { getTheme, Cinza100, Amarelo100 } from '../../shared/modules/layout';
import Api from '../modules/api';
import { primeiraLetraMaisculaCadaPalavra } from '../modules/string';
import { DadosAjuda, DadosFuncionario } from '../modules/types';
import BotaoDestaque from './BotaoDestaque';

interface State {
  campos: DadosAjuda;
  resultado: 'sucesso' | 'erro' | null;
  mensagemGeral: string;
  erros: { [key: string]: string };
  processando: boolean;
  termoLGPD: boolean;
}

interface Props {
  dadosFuncionario?: DadosFuncionario;
  empresaBancoId: string;
  nomeEmpresa: string;
  caminhoTermoPrivacidade: string;
  onClose: () => void;
  onNavegar?: () => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
}

const theme = getTheme();

export default class SolicitarAjudaForm extends React.Component<Props, State> {
  state: State = {
    campos: {
      aplicativo: 4,
      celular: '',
      cidade: '',
      codigoCliente: '',
      email: '',
      estado: '',
      idioma: 0,
      nome: '',
      nomeCliente: '',
      numeroIdentificador: '',
      texto: ''
    },
    erros: {},
    mensagemGeral: '',
    resultado: null,
    processando: false,
    termoLGPD: false
  };

  screenChangeDimensionsSubscription: EmitterSubscription | undefined;

  componentDidMount() {
    this.screenChangeDimensionsSubscription = Dimensions.addEventListener(
      'change',
      this.handleDimensionsChange
    );

    const { dadosFuncionario, empresaBancoId, nomeEmpresa } = this.props;

    let idioma = 0;
    if (getLocale() === 'en') idioma = 1;
    if (getLocale() === 'es') idioma = 2;

    this.setState({
      campos: {
        ...this.state.campos,
        idioma,
        nome: dadosFuncionario?.nome ?? '',
        email: dadosFuncionario?.email ?? '',
        celular: dadosFuncionario?.celular ?? '',
        cidade: dadosFuncionario?.cidade ?? '',
        estado: dadosFuncionario?.uf ?? '',
        numeroIdentificador: dadosFuncionario?.numeroIdentificador ?? '',
        codigoCliente: empresaBancoId,
        nomeCliente: nomeEmpresa
      }
    });
  }

  componentWillUnmount() {
    this.screenChangeDimensionsSubscription?.remove();
  }

  handleDimensionsChange = () => {
    this.forceUpdate();
  };

  handleChange = (campo: string, valor: any) => {
    this.setState(state => ({
      campos: { ...state.campos, [campo]: valor }
    }));
  };

  consistirDados = () => {
    const { campos } = this.state;

    const camposObrigatorios: { [key: string]: string } = {
      nome: campos.nome,
      email: campos.email,
      celular: campos.celular,
      cidade: campos.cidade,
      uf: campos.estado,
      texto: campos.texto
    };

    const erros: { [key: string]: string } = {};

    Object.keys(camposObrigatorios)
      .filter(key => !camposObrigatorios[key])
      .forEach(key => {
        Object.assign(erros, {
          [key]:
            translate(
              'O campo {0} é obrigatório',
              translate(primeiraLetraMaisculaCadaPalavra(key))
            ) + '.'
        });
      });

    this.setState({ erros });

    return Object.keys(erros).length === 0;
  };

  handleEnviar = async () => {
    if (!this.consistirDados()) {
      return;
    }

    const api = new Api();

    this.setState({ processando: true });

    await api.postDadosAjuda(this.state.campos, {
      onSuccess: r =>
        this.setState({
          processando: false,
          resultado: 'sucesso',
          mensagemGeral: r.mensagem
        }),
      onError: err => {
        if (err.name === 'TimeoutError' && this.props.onErroConexao) {
          this.props.onErroConexao(err.message, this.handleEnviar);
        } else {
          this.setState({
            processando: false,
            resultado: 'erro',
            mensagemGeral: err.message
          });
        }
      }
    });
  };

  renderLabel = () => {
    const textTerm = translate(
      'Li e estou de acordo com a # da Secullum Software.'
    );

    const textParts = textTerm.split('#');

    return (
      <Text nativeID="solicitar-ajuda-termo-privacidade">
        <Text>{`${textParts[0]} `}</Text>
        <Text
          style={styles.linkPrivacidade}
          onPress={() =>
            this.props.caminhoTermoPrivacidade?.length > 0 &&
            Linking.openURL(this.props.caminhoTermoPrivacidade)
          }
        >
          {translate('Política de Privacidade')}
        </Text>
        <Text>{` ${textParts[1]}`}</Text>
      </Text>
    );
  };

  render() {
    const { campos, erros, processando, termoLGPD, resultado, mensagemGeral } =
      this.state;

    // Campos flexíveis com folga de 10px das bordas do modal: desconta a margem do
    // modal (20*2), o padding do Card.Section (16*2) e a folga (10*2). Em tablet os
    // campos acompanham a largura da tela; nas demais plataformas mantém o teto de
    // 350. Calculado no render (e não no StyleSheet) para acompanhar rotação/resize.
    const larguraFormulario = {
      width:
        Platform.OS !== 'web' && isTablet()
          ? Dimensions.get('window').width - 92
          : Math.min(Dimensions.get('window').width - 92, 350)
    };

    return (
      <Form focusOn={[TextBox]} style={styles.container}>
        <View style={larguraFormulario}>
          <TextBox
            nativeID="solicitar-ajuda-nome"
            label={translate('Nome')}
            value={campos.nome}
            maxLength={100}
            returnKeyType={'next'}
            onChange={nome => this.handleChange('nome', nome)}
          />
          <ErrorMessage
            message={erros.nome}
            nativeID="mensagem-erro-campo-nome"
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-email"
            label={translate('Email')}
            value={campos.email}
            maxLength={100}
            keyboardType={'email-address'}
            returnKeyType={'next'}
            onChange={email => this.handleChange('email', email)}
          />
          <ErrorMessage
            message={erros.email}
            nativeID="mensagem-erro-campo-email"
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-celular"
            label={translate('Celular')}
            value={campos.celular}
            maxLength={20}
            keyboardType={'phone-pad'}
            returnKeyType={'next'}
            onChange={celular => this.handleChange('celular', celular)}
          />
          <ErrorMessage
            message={erros.celular}
            nativeID="mensagem-erro-campo-celular"
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-cidade"
            label={translate('Cidade')}
            value={campos.cidade}
            maxLength={50}
            returnKeyType={'next'}
            onChange={cidade => this.handleChange('cidade', cidade)}
          />
          <ErrorMessage
            message={erros.cidade}
            nativeID="mensagem-erro-campo-cidade"
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-uf"
            label={translate('UF')}
            value={campos.estado}
            maxLength={2}
            keyboardType={'default'}
            returnKeyType={'next'}
            onChange={estado => this.handleChange('estado', estado)}
          />
          <ErrorMessage message={erros.uf} nativeID="mensagem-erro-campo-uf" />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-identificador"
            label={translate('Nº Identificador')}
            value={campos.numeroIdentificador}
            keyboardType={'numeric'}
            returnKeyType={'next'}
            onChange={numeroIdentificador =>
              this.handleChange('numeroIdentificador', numeroIdentificador)
            }
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-codigo-empresa"
            label={translate('Código da Empresa')}
            value={campos.codigoCliente}
            keyboardType={'numeric'}
            returnKeyType={'next'}
            onChange={bancoId => this.handleChange('codigoCliente', bancoId)}
          />
          <Space />
          <TextBox
            nativeID="solicitar-ajuda-nome-empresa"
            label={translate('Nome da Empresa')}
            value={campos.nomeCliente}
            keyboardType={'default'}
            returnKeyType={'next'}
            onChange={nomeEmpresa =>
              this.handleChange('nomeCliente', nomeEmpresa)
            }
          />
        </View>
        <Space />
        <TextBox
          nativeID="solicitar-ajuda-texto"
          label={translate('Texto')}
          value={campos.texto || ''}
          maxLength={1000}
          multiline={Platform.OS === 'web'}
          keyboardType={'default'}
          returnKeyType={'next'}
          onChange={texto => this.handleChange('texto', texto)}
          style={Platform.OS === 'web' ? styles.textBox : larguraFormulario}
          inputStyle={Platform.OS === 'web' && styles.textBoxInput}
        />
        <ErrorMessage
          style={styles.textBox}
          message={erros.texto}
          nativeID="mensagem-erro-campo-texto"
        />
        <Space />
        <View style={larguraFormulario}>
          <CheckBox
            nativeID="solicitar-ajuda-lgpd"
            style={{ maxWidth: Dimensions.get('window').width - 80 }}
            renderLabel={this.renderLabel}
            value={termoLGPD}
            onChange={value => this.setState({ termoLGPD: value })}
          />
          <Space />
          <View style={styles.buttons}>
            <Button
              nativeID="solicitar-ajuda-cancelar"
              text={translate('Cancelar')}
              primary={false}
              style={styles.buttonCancel}
              onPress={this.props.onClose}
              disabled={processando}
            />
            <Space width={20} />
            <BotaoDestaque
              nativeID="solicitar-ajuda-enviar"
              text={translate('Enviar')}
              onPress={this.handleEnviar}
              style={styles.buttonSalvar}
              disabled={processando || !termoLGPD}
            />
          </View>
        </View>
        <Message
          nativeID="solicitar-ajuda-sucesso"
          message={translate(mensagemGeral)}
          visible={resultado === 'sucesso'}
          onRequestClose={() => {
            this.setState({ resultado: null, mensagemGeral: '' });
            this.props.onNavegar && this.props.onNavegar();
          }}
        />
        <Message
          nativeID="solicitar-ajuda-erro"
          type="warning"
          message={mensagemGeral}
          visible={resultado === 'erro'}
          onRequestClose={() =>
            this.setState({ resultado: null, mensagemGeral: '' })
          }
        />
      </Form>
    );
  }
}

const styles = StyleSheet.create({
  buttons: {
    flexDirection: 'row'
  },
  buttonCancel: {
    flex: 1,
    backgroundColor: theme.backgroundColor1
  },
  buttonSalvar: {
    flex: 1
  },
  textBox: {
    width: '100%'
  },
  textBoxInput: {
    minHeight: 60
  },
  linkPrivacidade: {
    fontSize: 12,
    color: Cinza100,
    textDecorationLine: 'underline',
    textDecorationColor: Amarelo100
  },
  container: {
    display: 'flex',
    alignItems: 'center'
  }
});
